import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faBook,
  faCircleCheck,
  faCircleExclamation,
  faCodeBranch,
  faComment,
  faDesktop,
  faEye,
  faEyeSlash,
  faKey,
  faLayerGroup,
  faMoon,
  faSpinner,
  faSun,
  faTv,
  faUpload,
  faUserPlus,
  faUsers,
  faXmark
} from '@fortawesome/free-solid-svg-icons';
import { authenticate, createUser, isFirstRun, getAllUsers, saveRememberMeToken, authenticateWithRememberMe, authenticateWithRememberMeAndMFA, clearLegacyAuth, type LocalSession, type LocalUser } from '@/services/local-auth';
import { migrateLegacyGitCredentials } from '@/services/api';

type LoginMode = 'login' | 'register' | 'mfa' | 'migrate';

function ErrorBox({ msg }: { msg: string }) {
  return (
    <div className="flex items-start gap-2.5 p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl">
      <FontAwesomeIcon icon={faCircleExclamation} className="h-4 w-4 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
      <p className="text-sm text-red-600 dark:text-red-300">{msg}</p>
    </div>
  );
}

function SuccessBox({ msg }: { msg: string }) {
  return (
    <div className="flex items-start gap-2.5 p-3 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-xl">
      <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4 text-emerald-500 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
      <p className="text-sm text-emerald-600 dark:text-emerald-300">{msg}</p>
    </div>
  );
}

export interface LoginProps {
  onLogin: (session: LocalSession) => void;
  onSkip?: () => void;
  initialMode?: LoginMode;
  migrateEmail?: string;
}

const inputCls = 'w-full px-3.5 py-2.5 bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/[0.08] rounded-xl text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/30 focus:border-indigo-400 dark:focus:border-indigo-500/40 transition-all text-sm';
const labelCls = 'block text-[10px] font-medium text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1.5';
const btnPrimary = 'w-full py-2.5 px-4 bg-slate-900 dark:bg-white/[0.1] hover:bg-slate-800 dark:hover:bg-white/[0.15] text-white font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 text-sm border border-transparent dark:border-white/[0.06]';

export function Login({ onLogin, onSkip, initialMode, migrateEmail }: LoginProps) {
  const [mode, setMode] = useState<LoginMode>(initialMode ?? 'login');
  const [firstRun, setFirstRun] = useState(false);
  const [email, setEmail] = useState(migrateEmail ?? '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [pendingPassword, setPendingPassword] = useState('');
  const [localUsers, setLocalUsers] = useState<LocalUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<LocalUser | null>(null);
  const [pendingRememberMeUserId, setPendingRememberMeUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPassword, setImportPassword] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [themeMode, setThemeMode] = useState<'light' | 'dark' | 'system'>(() => {
    const stored = localStorage.getItem('verigit-theme');
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
    // Migrate old boolean flag
    const oldFlag = localStorage.getItem('verigit-dark-mode');
    if (oldFlag === 'true') return 'dark';
    if (oldFlag === 'false') return 'light';
    return 'system';
  });

  useEffect(() => {
    Promise.all([isFirstRun(), getAllUsers()]).then(([first, users]) => {
      setFirstRun(first);
      setLocalUsers(users);
      if (first && !initialMode) setMode('register');
      // Pre-fill email when exactly one local account exists and no email was passed in
      if (!migrateEmail && !first && users.length === 1 && !email) {
        setEmail(users[0].email);
        setSelectedUser(users[0]);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMode]);

  useEffect(() => {
    const root = document.documentElement;
    const apply = (dark: boolean) => root.classList.toggle('dark', dark);
    localStorage.setItem('verigit-theme', themeMode);
    if (themeMode === 'dark') {
      localStorage.setItem('verigit-dark-mode', 'true');
      apply(true);
    } else if (themeMode === 'light') {
      localStorage.setItem('verigit-dark-mode', 'false');
      apply(false);
    } else {
      localStorage.removeItem('verigit-dark-mode');
      apply(window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => { if (themeMode === 'system') apply(e.matches); };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [themeMode]);

  const cycleTheme = () => setThemeMode(p => p === 'system' ? 'light' : p === 'light' ? 'dark' : 'system');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) { setError('Please enter your email and password.'); return; }
    setLoading(true); setError(null);
    try {
      const result = await authenticate(email.trim(), password);
      if (result.requiresMFA) {
        setPendingEmail(email.trim());
        setPendingPassword(password);
        setMode('mfa');
        return;
      }
      if (!result.success || !result.session) { setError(result.error ?? 'Login failed.'); return; }
      saveRememberMeToken(result.session.userId);
      setSuccess(true);
      onLogin(result.session);
    } catch (err: any) {
      setError(err.message ?? 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleMFA = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!totpCode.trim()) { setError('Please enter the 6-digit code.'); return; }
    setLoading(true); setError(null);
    try {
      let result;
      if (pendingRememberMeUserId) {
        result = await authenticateWithRememberMeAndMFA(pendingRememberMeUserId, totpCode.replace(/\s/g, ''));
      } else {
        result = await authenticate(pendingEmail, pendingPassword, totpCode.replace(/\s/g, ''));
      }
      if (!result.success || !result.session) { setError(result.error ?? 'Invalid code.'); return; }
      if (pendingRememberMeUserId) saveRememberMeToken(pendingRememberMeUserId);
      setSuccess(true);
      onLogin(result.session);
    } catch (err: any) {
      setError(err.message ?? 'Verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { setError('Please enter an email address.'); return; }
    if (!password) { setError('Please enter a password.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    setLoading(true); setError(null);
    try {
      await createUser(email.trim(), password, displayName.trim() || undefined, mode === 'migrate' ? 'legacy' : undefined);
      const result = await authenticate(email.trim(), password);
      if (!result.success || !result.session) { setError('Account created but login failed — try signing in.'); setMode('login'); return; }
      if (mode === 'migrate') {
        await migrateLegacyGitCredentials();
        clearLegacyAuth();
      }
      saveRememberMeToken(result.session.userId);
      setSuccess(true);
      onLogin(result.session);
    } catch (err: any) {
      setError(err.message ?? 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  // Keep pendingEmail/pendingPassword used (MFA flow stores them)
  void pendingEmail; void pendingPassword;

  const features = [
    { icon: <FontAwesomeIcon icon={faUsers} className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />, bg: 'bg-emerald-500/10 dark:bg-emerald-500/15', label: 'Every Kind of Team', desc: 'devs, ops, marketing, content & more' },
    { icon: <FontAwesomeIcon icon={faLayerGroup} className="h-4 w-4 text-blue-500 dark:text-blue-400" />, bg: 'bg-blue-500/10 dark:bg-blue-500/15', label: 'Project Management', desc: 'Jira, Linear, Monday, Trello' },
    { icon: <FontAwesomeIcon icon={faBook} className="h-4 w-4 text-cyan-500 dark:text-cyan-400" />, bg: 'bg-cyan-500/10 dark:bg-cyan-500/15', label: 'Documentation', desc: 'Confluence, Notion & Google Docs' },
    { icon: <FontAwesomeIcon icon={faCodeBranch} className="h-4 w-4 text-violet-500 dark:text-violet-400" />, bg: 'bg-violet-500/10 dark:bg-violet-500/15', label: 'Git Analytics', desc: 'commits, branches & author stats' },
    { icon: <FontAwesomeIcon icon={faTv} className="h-4 w-4 text-rose-500 dark:text-rose-400" />, bg: 'bg-rose-500/10 dark:bg-rose-500/15', label: 'Live Dashboards', desc: 'TV mode for the big screen' },
    { icon: <FontAwesomeIcon icon={faComment} className="h-4 w-4 text-amber-500 dark:text-amber-400" />, bg: 'bg-amber-500/10 dark:bg-amber-500/15', label: 'Chat & Messaging', desc: 'Slack & Teams channel monitoring — coming soon' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0a0a0f] flex items-center justify-center p-4 relative overflow-hidden transition-colors" style={{ WebkitAppRegion: 'drag' } as React.CSSProperties}>
      <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full opacity-40 dark:opacity-30 blur-[120px] animate-pulse" style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.5) 0%, rgba(139,92,246,0.3) 40%, transparent 70%)' }} />
      <div className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] rounded-full opacity-35 dark:opacity-25 blur-[100px] animate-pulse" style={{ background: 'radial-gradient(circle, rgba(236,72,153,0.5) 0%, rgba(244,114,182,0.3) 40%, transparent 70%)', animationDelay: '2s' }} />
      <div className="absolute top-[30%] right-[20%] w-[300px] h-[300px] rounded-full opacity-30 dark:opacity-20 blur-[80px] animate-pulse" style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.5) 0%, rgba(6,182,212,0.3) 40%, transparent 70%)', animationDelay: '4s' }} />
      <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(0,0,0,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.06) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
      <div className="relative z-10 w-full max-w-[1040px] flex rounded-3xl overflow-hidden shadow-2xl shadow-black/10 dark:shadow-black/40" style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}>
        <div className="absolute -inset-[1px] rounded-3xl opacity-30 dark:opacity-50" style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.5), rgba(236,72,153,0.5), rgba(250,204,21,0.3), rgba(34,211,238,0.5), rgba(139,92,246,0.5))' }} />
        <div className="hidden lg:flex lg:w-[45%] relative flex-col justify-center px-10 py-10 bg-white/70 dark:bg-white/[0.02] backdrop-blur-xl border-r border-black/[0.06] dark:border-white/[0.06]">
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-2">
              <img src="/verigit.png" alt="VeriGit" className="h-10" />
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">VeriGit</h1>
            </div>
            <p className="text-slate-500 dark:text-slate-400 text-sm mb-10">Team analytics for every kind of team.</p>
            <div className="space-y-4">
              {features.map(({ icon, bg, label, desc }) => (
                <div key={label} className="flex items-center gap-3 group">
                  <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110`}>
                    {icon}
                  </div>
                  <div className="text-sm">
                    <span className="text-slate-800 dark:text-white/90 font-medium">{label}</span>
                    <span className="text-slate-400 dark:text-slate-500 ml-1.5">— {desc}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10 pt-6 border-t border-black/[0.06] dark:border-white/[0.06]">
              <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-widest">Connect Jira, GitHub, Confluence, Notion, Linear &amp; more in Settings</p>
            </div>
          </div>
        </div>
        <div className="flex-1 bg-white/80 dark:bg-white/[0.04] backdrop-blur-2xl px-8 py-10 lg:px-10 relative">
          <div className="w-full max-w-sm mx-auto">
            <div className="lg:hidden flex items-center gap-3 mb-8">
              <img src="/verigit.png" alt="VeriGit" className="h-9" />
              <span className="text-lg font-bold text-slate-900 dark:text-white">VeriGit</span>
            </div>
            {mode === 'mfa' && (
              <>
                <button onClick={() => { setMode('login'); setTotpCode(''); setError(null); }} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 mb-6 transition-colors">
                  <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" /> Back
                </button>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-indigo-500/10 dark:bg-indigo-500/15 flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon icon={faKey} className="h-5 w-5 text-indigo-500 dark:text-indigo-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">Two-factor authentication</h2>
                    <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">Enter the code from your authenticator app</p>
                  </div>
                </div>
                <form onSubmit={handleMFA} className="space-y-4">
                  <div>
                    <label className={labelCls}>Authenticator Code</label>
                    <input type="text" inputMode="numeric" autoComplete="one-time-code" value={totpCode} onChange={e => setTotpCode(e.target.value)} placeholder="000 000" maxLength={7} className={`${inputCls} text-center text-lg tracking-[0.4em] font-mono`} autoFocus disabled={loading || success} />
                    <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1.5">Open your authenticator app and enter the 6-digit code.</p>
                  </div>
                  {error && <ErrorBox msg={error} />}
                  {success && <SuccessBox msg="Verified! Loading dashboard..." />}
                  <button type="submit" disabled={loading || success} className={btnPrimary}>
                    {loading ? <><FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> Verifying...</> : success ? <><FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4" /> Verified</> : 'Verify Code'}
                  </button>
                </form>
              </>
            )}
            {mode === 'login' && (
              <>
                {/* Recent Accounts */}
                {localUsers.length > 0 && (
                  <div className="mb-7">
                    <p className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-3">Recent Accounts</p>
                    <div className="flex flex-wrap gap-3">
                      {localUsers.map(user => {
                        const initials = (user.displayName || user.email)
                          .split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase() ?? '').join('') ||
                          user.email[0].toUpperCase();
                        const hue = user.email.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360;
                        const isSelected = selectedUser?.id === user.id;
                        return (
                          <button
                            key={user.id}
                            type="button"
                            onClick={async () => {
                              setSelectedUser(user);
                              setEmail(user.email);
                              setError(null);
                              setLoading(true);
                              try {
                                const result = await authenticateWithRememberMe(user.id);
                                if (result.success && result.session) {
                                  saveRememberMeToken(user.id);
                                  setSuccess(true);
                                  onLogin(result.session);
                                } else if (result.requiresMFA) {
                                  setPendingRememberMeUserId(user.id);
                                  setPendingEmail(user.email);
                                  setPendingPassword('');
                                  setMode('mfa');
                                } else {
                                  // No remember-me token yet — fall back to password
                                  setPassword('');
                                  setTimeout(() => document.getElementById('password')?.focus(), 50);
                                }
                              } catch {
                                setPassword('');
                                setTimeout(() => document.getElementById('password')?.focus(), 50);
                              } finally {
                                setLoading(false);
                              }
                            }}
                            className={`flex flex-col items-center gap-1.5 p-2 rounded-xl transition-all ${
                              isSelected
                                ? 'bg-indigo-50 dark:bg-indigo-500/10 ring-2 ring-indigo-400/50'
                                : 'hover:bg-slate-100 dark:hover:bg-white/[0.06]'
                            }`}
                          >
                            <div
                              className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 shadow-sm"
                              style={{ background: `hsl(${hue},55%,48%)` }}
                            >
                              {initials}
                            </div>
                            <span className="text-[10px] text-slate-600 dark:text-slate-300 max-w-[60px] truncate">
                              {user.displayName || user.email.split('@')[0]}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">Welcome back</h2>
                <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">Sign in to your VeriGit account</p>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label htmlFor="email" className={labelCls}>Email</label>
                    <input id="email" type="email" autoComplete="email" value={email} onChange={e => { setEmail(e.target.value); setSelectedUser(null); }} placeholder="you@company.com" className={inputCls} disabled={loading || success} autoFocus={localUsers.length === 0} />
                  </div>
                  <div>
                    <label htmlFor="password" className={labelCls}>Password</label>
                    <div className="relative">
                      <input id="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" className={`${inputCls} pr-10`} disabled={loading || success} />
                      <button type="button" onClick={() => setShowPassword(v => !v)} tabIndex={-1} className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                        {showPassword ? <FontAwesomeIcon icon={faEyeSlash} className="h-4 w-4" /> : <FontAwesomeIcon icon={faEye} className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  {error && <ErrorBox msg={error} />}
                  {success && <SuccessBox msg="Signed in! Loading dashboard..." />}
                  <button type="submit" disabled={loading || success} className={btnPrimary}>
                    {loading ? <><FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> Signing in...</> : success ? <><FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4" /> Signed in</> : 'Sign in'}
                  </button>
                </form>
                {!firstRun && (
                  <div className="mt-4 text-center">
                    <button type="button" onClick={() => { setMode('register'); setError(null); setPassword(''); setConfirmPassword(''); }} className="text-xs text-indigo-500 dark:text-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-300 transition-colors flex items-center gap-1.5 mx-auto">
                      <FontAwesomeIcon icon={faUserPlus} className="h-3.5 w-3.5" /> Create a new account
                    </button>
                  </div>
                )}
              </>
            )}
            {(mode === 'register' || mode === 'migrate') && (
              <>
                {!firstRun && (
                  <button onClick={() => { setMode('login'); setError(null); setPassword(''); setConfirmPassword(''); }} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 mb-6 transition-colors">
                    <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" /> Back to sign in
                  </button>
                )}
                {mode === 'migrate' ? (
                  <>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                        <FontAwesomeIcon icon={faKey} className="h-5 w-5 text-amber-500" />
                      </div>
                      <h2 className="text-xl font-bold text-slate-900 dark:text-white">Set up your account</h2>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">VeriGit now uses email &amp; password login. Create a password to migrate your existing session. Your integrations stay intact.</p>
                  </>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">{firstRun ? 'Create your account' : 'New account'}</h2>
                    <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">{firstRun ? 'Set up VeriGit for the first time' : 'Register a new VeriGit account'}</p>
                  </>
                )}
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <label htmlFor="reg-email" className={labelCls}>Email</label>
                    <input id="reg-email" type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" className={inputCls} disabled={loading || success || mode === 'migrate'} autoFocus={!migrateEmail} />
                  </div>
                  <div>
                    <label htmlFor="reg-name" className={labelCls}>Display Name <span className="normal-case font-normal text-slate-400">(optional)</span></label>
                    <input id="reg-name" type="text" autoComplete="name" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Your name" className={inputCls} disabled={loading || success} />
                  </div>
                  <div>
                    <label htmlFor="reg-password" className={labelCls}>Password</label>
                    <div className="relative">
                      <input id="reg-password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters" className={`${inputCls} pr-10`} disabled={loading || success} />
                      <button type="button" onClick={() => setShowPassword(v => !v)} tabIndex={-1} className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                        {showPassword ? <FontAwesomeIcon icon={faEyeSlash} className="h-4 w-4" /> : <FontAwesomeIcon icon={faEye} className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="reg-confirm" className={labelCls}>Confirm Password</label>
                    <input id="reg-confirm" type="password" autoComplete="new-password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Repeat password" className={inputCls} disabled={loading || success} />
                  </div>
                  {error && <ErrorBox msg={error} />}
                  {success && <SuccessBox msg="Account created! Loading dashboard..." />}
                  <button type="submit" disabled={loading || success} className={btnPrimary}>
                    {loading ? <><FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> Creating account...</> : success ? <><FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4" /> Done</> : mode === 'migrate' ? 'Migrate account' : 'Create account'}
                  </button>
                  {mode === 'register' && (
                    <div className="flex items-start gap-2.5 px-3.5 py-3 rounded-xl bg-amber-50 dark:bg-amber-500/[0.08] border border-amber-200/70 dark:border-amber-500/20">
                      <FontAwesomeIcon icon={faCircleExclamation} className="h-3.5 w-3.5 text-amber-500 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                      <p className="text-[11px] text-amber-700 dark:text-amber-300 leading-relaxed">
                        Your account is stored <strong>only in this browser</strong>. To use VeriGit on another device, export your profile from <strong>Settings → Data &amp; Backup</strong> and import it there.
                      </p>
                    </div>
                  )}
                </form>
              </>
            )}
            {onSkip && mode !== 'mfa' && (
              <div className="mt-4 text-center">
                <button type="button" onClick={onSkip} className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors underline underline-offset-2">
                  Skip — configure integrations later in Settings
                </button>
              </div>
            )}
            <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
              <p className="text-[11px] text-slate-400">All data stays on your device.</p>
              <button type="button" onClick={() => setShowImportModal(true)} className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-medium text-indigo-500 dark:text-indigo-400 hover:bg-indigo-500/[0.06] border border-transparent hover:border-indigo-500/[0.15] transition-all">
                <FontAwesomeIcon icon={faUpload} className="h-3 w-3" /> Import Profile
              </button>
              <button type="button" onClick={cycleTheme} title={`Theme: ${themeMode}`} className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-medium text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] border border-transparent hover:border-black/[0.06] dark:hover:border-white/[0.06] transition-all">
                {themeMode === 'system' ? <FontAwesomeIcon icon={faDesktop} className="h-3 w-3" /> : themeMode === 'dark' ? <FontAwesomeIcon icon={faMoon} className="h-3 w-3 text-indigo-400" /> : <FontAwesomeIcon icon={faSun} className="h-3 w-3 text-amber-400" />}
                {themeMode === 'system' ? 'System' : themeMode === 'dark' ? 'Dark' : 'Light'}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}>
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-black/10 dark:border-white/10 w-full max-w-sm mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-slate-900 dark:text-white">Import Profile</h3>
              <button onClick={() => { setShowImportModal(false); setImportFile(null); setImportPassword(''); setImportError(null); }} className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors">
                <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Import a <code className="text-[10px] px-1 py-0.5 bg-slate-100 dark:bg-slate-800 rounded">.verigit</code> profile file to restore settings and credentials.</p>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Profile File</label>
                <label className="flex items-center justify-center gap-2 w-full px-3 py-2.5 bg-slate-50 dark:bg-white/[0.04] border border-dashed border-slate-300 dark:border-white/[0.12] rounded-xl cursor-pointer hover:border-indigo-400 transition-all text-sm">
                  <FontAwesomeIcon icon={faUpload} className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-500 dark:text-slate-400 truncate">{importFile ? importFile.name : 'Choose .verigit file...'}</span>
                  <input type="file" accept=".verigit" className="hidden" onChange={e => { setImportFile(e.target.files?.[0] || null); setImportError(null); }} />
                </label>
              </div>
              <div>
                <label className={labelCls}>Password</label>
                <input type="password" value={importPassword} onChange={e => { setImportPassword(e.target.value); setImportError(null); }} placeholder="Enter export password" className={inputCls} />
              </div>
              {importError && (
                <div className="flex items-start gap-2 p-2.5 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl">
                  <FontAwesomeIcon icon={faCircleExclamation} className="h-3.5 w-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-600 dark:text-red-300">{importError}</p>
                </div>
              )}
              <button disabled={!importFile || !importPassword || importLoading} onClick={async () => {
                if (!importFile || !importPassword) return;
                setImportLoading(true); setImportError(null);
                try {
                  const { importProfile } = await import('@/services/profile-export');
                  await importProfile(importFile, importPassword);
                  window.location.reload();
                } catch (err) { setImportError((err as Error).message); }
                finally { setImportLoading(false); }
              }} className="w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-xl disabled:opacity-50 transition-all text-sm flex items-center justify-center gap-2">
                {importLoading ? <><FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> Importing...</> : <><FontAwesomeIcon icon={faUpload} className="h-4 w-4" /> Import &amp; Sign In</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;

